import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { Button, Modal } from '@bettercomp/ui-components';
import { logout } from 'service/cognitoService';
import { createAuthBroadcastChannel, AuthBroadcastMessageType } from './constants';
import cn from './styles.module.scss';

const IDLE_LOGOUT_TIMEOUT = 30 * 60 * 1000;
const IDLE_WARNING_TIMEOUT = IDLE_LOGOUT_TIMEOUT - 3 * 60 * 1000;

export const IdleWarning = () => {
  const [visible, setVisible] = useState(false);
  const logoutTimeout = useRef<ReturnType<typeof setTimeout>>();
  const idleWarningTimeout = useRef<ReturnType<typeof setTimeout>>();
  const channel = useMemo(() => createAuthBroadcastChannel(), [])

  const onLogoutTimeout = () => {
    logout();
  };

  const onIdleWarningTimeout = () => {
    setVisible(true);
  };

  const resetTimer = useCallback((force: boolean = false) => {
    if (force || !visible) {

      if (idleWarningTimeout.current) {
        clearTimeout(idleWarningTimeout.current);
      }

      if (logoutTimeout.current) {
        clearTimeout(logoutTimeout.current);
      }

      idleWarningTimeout.current = setTimeout(onIdleWarningTimeout, IDLE_WARNING_TIMEOUT);
      logoutTimeout.current = setTimeout(onLogoutTimeout, IDLE_LOGOUT_TIMEOUT);
    }
  }, [visible]);

  useEffect(() => {
    resetTimer(true);
    return () => {
      channel.close();
    }
  }, [channel, resetTimer]);

  useEffect(() => {
    // handle clicks, that comes from another tab
    const authChannelMessageHandler = (message: { data: AuthBroadcastMessageType }) => {
      if (message.data === AuthBroadcastMessageType.Up) {
        setVisible(false);
        resetTimer(true)
      }
      if (message.data === AuthBroadcastMessageType.Logout) {
        logout(false);
      }
    };

    const activityHandler = () => {
      // notify other tabs about clicks
      channel.postMessage(AuthBroadcastMessageType.Up);
      resetTimer();
    }

    channel.addEventListener('message', authChannelMessageHandler);
    document.addEventListener('click', activityHandler);

    return () => {
      channel.removeEventListener('message', authChannelMessageHandler);
      document.removeEventListener('click', activityHandler);
    };
  }, [channel, resetTimer]);

  const handleConfirm = async () => {
    setVisible(false);
    resetTimer(true);
  };

  return (
    <Modal
      open={visible}
      closable={false}
    >
      <div className={cn.container}>
        <div>Your session will expire soon</div>
        <Button type="primary" onClick={handleConfirm}>Keep Me Logged in</Button>
      </div>
    </Modal>
  );
};
